import { withDevtools } from '@angular-architects/ngrx-toolkit';
import { computed, inject, Signal } from '@angular/core';
import { NewsCommunity, NewsCommunityApproveAutomaticallyRequest } from '@models/news-article';
import { patchState, signalStore, withComputed, withMethods, withState } from '@ngrx/signals';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import { catchError, EMPTY, pipe, switchMap, tap } from 'rxjs';
import { getAll, getOne, StateDictionary } from '../../helpers';
import { NewsCommunitiesService } from './news-communities.service';

type NewsCommunitiesState = {
  newsCommunities: NewsCommunity[];
  isLoading: boolean;
  selectedNewsCommunity: NewsCommunity | null;
  selectedNewsCommunityLoading: boolean;
};

const initialState: NewsCommunitiesState = {
  newsCommunities: [],
  isLoading: false,
  selectedNewsCommunity: null,
  selectedNewsCommunityLoading: false,
};

const newsCommunitiesDictionary: StateDictionary<NewsCommunity> = {
  entityList: 'newsCommunities',
  listLoading: 'isLoading',
  selectedEntity: 'selectedNewsCommunity',
  selectedEntityLoading: 'selectedNewsCommunityLoading',
  entityId: 'id',
  createLoading: '',
  sortFn: (a, b) => a.name.localeCompare(b.name),
};
export const NewsCommunitiesStore = signalStore(
  { providedIn: 'root' },
  withState(initialState),
  withDevtools('news-communities-store'),

  withComputed(store => ({
    selectedNewsCommunityId: computed(() => store.selectedNewsCommunity()?.id || null),
  })),

  withMethods((store, newsCommunitiesService = inject(NewsCommunitiesService)) => {
    const repositoryMethods = {
      getAll: getAll<NewsCommunity>({
        store,
        service: newsCommunitiesService,
        dictionary: newsCommunitiesDictionary,
      }),
      getOne: getOne<NewsCommunity>({
        store,
        service: newsCommunitiesService,
        dictionary: newsCommunitiesDictionary,
        onErrorCallback() {
          patchState(store, { selectedNewsCommunity: null });
        },
      }),
      toggleAutomaticApproval: rxMethod<{ id: string; req: NewsCommunityApproveAutomaticallyRequest }>(
        pipe(
          tap(() => patchState(store, { selectedNewsCommunityLoading: true })),
          switchMap(({ id, req }) =>
            newsCommunitiesService.toggleAutomaticApproval(id, req).pipe(
              tap(newsCommunity => {
                patchState(store, {
                  selectedNewsCommunity: newsCommunity,
                  selectedNewsCommunityLoading: false,
                  newsCommunities: store.newsCommunities().map(item => (item.id === newsCommunity.id ? newsCommunity : item)),
                });
              }),
              catchError(() => {
                patchState(store, { selectedNewsCommunityLoading: false });
                return EMPTY;
              }),
            ),
          ),
        ),
      ),
    };
    const selectNewsCommunity = (id: string) => {
      const selected = store.newsCommunities().find(at => at.id === id) || null;
      patchState(store, { selectedNewsCommunity: selected });
      if (!selected) repositoryMethods.getOne(id);
    };

    const resetState = (state: Partial<NewsCommunitiesState> = {}) => {
      patchState(store, { ...initialState, ...state });
    };
    return {
      ...repositoryMethods,
      selectNewsCommunity,
      resetState,
    };
  }),
);
export interface NewsCommunitiesStoreType {
  readonly newsCommunities: Signal<NewsCommunity[]>;
  readonly isLoading: Signal<boolean>;
  readonly selectedNewsCommunityLoading: Signal<boolean>;
  readonly selectedNewsCommunity: Signal<NewsCommunity | null>;

  readonly selectedNewsCommunityId: Signal<string | null>;

  readonly getAll: () => void;
  readonly getOne: (id: string) => void;
  readonly toggleAutomaticApproval: (input: { id: string; req: NewsCommunityApproveAutomaticallyRequest }) => void;
  readonly selectNewsCommunity: (id: string) => void;
}
